// src/utils/dateTimeUtils.js

/**
 * Utility for parsing and formatting dates/times in a consistent, locale-aware manner.
 * Future-proofed for easy locale and timezone customization.
 */

/**
 * Maps an i18next language code to the locale + IANA timezone we want to
 * render dates/times in. Timestamps are stored in UTC, so this is what shifts
 * them into the user's local presentation (e.g. Japanese users see JST).
 */
const LANGUAGE_DATETIME_CONFIG = {
  en: { locale: "en-GB", timeZone: "Europe/London" },
  "en-GB": { locale: "en-GB", timeZone: "Europe/London" },
  ja: { locale: "ja-JP", timeZone: "Asia/Tokyo" },
  "ja-JP": { locale: "ja-JP", timeZone: "Asia/Tokyo" },
};

const DEFAULT_DATETIME_CONFIG = LANGUAGE_DATETIME_CONFIG["en-GB"];

/**
 * Resolve the locale + timezone for an i18next language code, tolerating
 * regioned tags (e.g. "ja-JP" → "ja") and unknown values.
 * @param {string} [language]
 * @returns {{ locale: string, timeZone: string }}
 */
export function getDateTimeConfigForLanguage(language) {
  if (!language) return DEFAULT_DATETIME_CONFIG;
  return (
    LANGUAGE_DATETIME_CONFIG[language] ||
    LANGUAGE_DATETIME_CONFIG[language.split("-")[0]] ||
    DEFAULT_DATETIME_CONFIG
  );
}

/**
 * Parse an input into a JS Date object.
 * Supports:
 *  - Date objects (returned as-is)
 *  - numbers (treated as ms since epoch)
 *  - ISO strings or other date strings
 *  - date-only strings YYYY-MM-DD
 *  - time-only strings HH:MM or HH:MM:SS or with milliseconds
 * @param {Date|number|string} input
 * @returns {Date|null} JS Date or null if invalid
 */
export function parseDate(input) {
  if (input == null) return null;

  // Firestore Timestamp support (optional, harmless if not used)
  if (typeof input === "object" && input) {
    // Timestamp-like object
    if (typeof input.toDate === "function") {
      const d = input.toDate();
      return isNaN(d.valueOf()) ? null : d;
    }
    if (typeof input.seconds === "number") {
      const d = new Date(input.seconds * 1000);
      return isNaN(d.valueOf()) ? null : d;
    }
  }

  if (input instanceof Date) {
    return isNaN(input.valueOf()) ? null : input;
  }

  if (typeof input === "number") {
    const d = new Date(input);
    return isNaN(d.valueOf()) ? null : d;
  }

  if (typeof input === "string") {
    let s = input.trim();

    // Normalize “filename-safe ISO” like 2025-10-13T14-43-32.163Z → proper ISO
    // Also tolerate missing trailing Z.
    s = s.replace(
      /^(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})(\.\d{1,3})?(Z)?$/,
      (_, d, hh, mm, ss, ms = "", z = "Z") => `${d}T${hh}:${mm}:${ss}${ms}${z}`
    );

    // Allow space instead of 'T'
    s = s.replace(
      /^(\d{4}-\d{2}-\d{2}) (\d{2}):(\d{2}):(\d{2})(\.\d{1,3})?$/,
      (_, d, hh, mm, ss, ms = "") => `${d}T${hh}:${mm}:${ss}${ms}Z`
    );

    // Try native parse
    const d1 = new Date(s);
    if (!isNaN(d1.valueOf())) return d1;

    // date-only YYYY-MM-DD
    let match = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, y, m, day] = match;
      const dd = new Date(Date.UTC(+y, +m - 1, +day));
      return isNaN(dd.valueOf()) ? null : dd;
    }

    // time-only HH:MM or HH:MM:SS(.fff)
    match = s.match(/^(\d{2}):(\d{2})(?::(\d{2})(\.\d{1,3})?)?$/);
    if (match) {
      const [, hr, min, sec = "00", ms = ""] = match;
      const iso = `1970-01-01T${hr}:${min}:${sec}${ms}Z`;
      const td = new Date(iso);
      return isNaN(td.valueOf()) ? null : td;
    }
  }

  return null;
}

/**
 * Format a date portion according to locale and timezone.
 * @param {Date|number|string} input
 * @param {string} [locale='en-GB'] BCP 47 language tag
 * @param {string} [timeZone] IANA timezone (defaults to runtime)
 * @returns {string|null} formatted date or null if invalid
 */
export function formatDate(input, locale = "en-GB", timeZone) {
  const d = parseDate(input);
  if (!d) return null;
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone,
  }).format(d);
}

/**
 * Format a time portion according to locale and timezone.
 * Accepts full Date or time-only string.
 * Drops any seconds or millisecond component—returns HH:MM.
 * @param {Date|number|string} input
 * @param {string} [locale='en-GB'] BCP 47 language tag
 * @param {string} [timeZone] IANA timezone (defaults to runtime)
 * @returns {string|null} formatted time (HH:MM) or null if invalid
 */
export function formatTime(input, locale = "en-GB", timeZone) {
  // If input is a time-only string, strip seconds/milliseconds and return HH:MM
  if (
    typeof input === "string" &&
    input.match(/^\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?$/)
  ) {
    // Keep only HH:MM
    const stripped = input.replace(/^(\d{2}:\d{2})(?::.*)?$/, "$1");
    return stripped;
  }
  const d = parseDate(input);
  if (!d) return null;
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  }).format(d);
}

/**
 * Format both date and time into separate fields.
 * @param {Date|number|string} input
 * @param {object} [options]
 * @param {string} [options.locale='en-GB']
 * @param {string} [options.timeZone]
 * @returns {{ date: string|null, time: string|null }}
 */
export function formatDateTime(input, { locale = "en-GB", timeZone } = {}) {
  return {
    date: formatDate(input, locale, timeZone),
    time: formatTime(input, locale, timeZone),
  };
}
